import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { BaseWidgetComponent } from '../components/strategy-grid/base-widget/base-widget.component';
import { GridsterItem, GridsterItemComponent } from 'angular-gridster2';
import { NumberRange } from 'scichart';

import { AssetsWidgetComponent } from '../components/strategy-grid/assets-widget/assets-widget.component';
import { BreakoutWidgetComponent } from '../components/strategy-grid/breakout-widget/breakout-widget.component';
import { BuildsWidgetComponent } from '../components/strategy-grid/builds-widget/builds-widget.component';
import { BuildDetailWidgetComponent } from '../components/strategy-grid/build-detail-widget/build-detail-widget.component';
import { DefinitionsWidgetComponent } from '../components/strategy-grid/definitions-widget/definitions-widget.component';
import { DeploysWidgetComponent } from '../components/strategy-grid/deploys-widget/deploys-widget.component';
import { ExperimentsWidgetComponent } from '../components/strategy-grid/experiments-widget/experiments-widget.component';
import { FeatureSelectorWidgetComponent } from '../components/strategy-grid/feature-selector-widget/feature-selector-widget.component';
import { GpusWidgetComponent } from '../components/strategy-grid/gpus-widget/gpus-widget.component';
import { HostsWidgetComponent } from '../components/strategy-grid/hosts-widget/hosts-widget.component';
import { FilesWidgetComponent } from '../components/strategy-grid/files-widget/files-widget.component';
import { InspectWidgetComponent } from '../components/strategy-grid/inspect-widget/inspect-widget.component';
import { LabWidgetComponent } from '../components/strategy-grid/lab-widget/lab-widget.component';
import { ModelChartWidgetComponent } from '../components/strategy-grid/model-chart-widget/model-chart-widget.component';
import { ModelInputsWidgetComponent } from '../components/strategy-grid/model-inputs-widget/model-inputs-widget.component';
import { PacketWidgetComponent } from '../components/strategy-grid/packet-widget/packet-widget.component';
import { ChatWidgetComponent } from '../components/strategy-grid/chat-widget/chat-widget.component';
import { SparklinesWidgetComponent } from '../components/strategy-grid/sparklines-widget/sparklines-widget.component';
import { RawDataWidgetComponent } from '../components/strategy-grid/raw-data-widget/raw-data-widget.component';
import { FeatureEditorWidgetComponent } from '../components/strategy-grid/feature-editor-widget/feature-editor-widget.component';
import { SchemasWidgetComponent } from '../components/strategy-grid/schemas-widget/schemas-widget.component';
import { StrategyWidgetComponent } from '../components/strategy-grid/strategy-widget/strategy-widget.component';
import { StreamWidgetComponent } from '../components/strategy-grid/stream-widget/stream-widget.component';
import { TerminalWidgetComponent } from '../components/strategy-grid/terminal-widget/terminal-widget.component';
import { TickWidgetComponent } from '../components/strategy-grid/tick-widget/tick-widget.component';
import { VaultWidgetComponent } from '../components/strategy-grid/vault-widget/vault-widget.component';
import { VaultDetailWidgetComponent } from '../components/strategy-grid/vault-detail-widget/vault-detail-widget.component';
import { PlaygroundWidgetComponent } from '../components/strategy-grid/playground-widget/playground-widget.component';
import { ModelDiffWidgetComponent } from '../components/strategy-grid/model-diff-widget/model-diff-widget.component';
import { IndicatorWidgetComponent } from '../components/strategy-grid/indicator-widget/indicator-widget.component';
import { SettingsWidgetComponent } from '../components/strategy-grid/settings-widget/settings-widget.component';
import { MonacoWidgetComponent } from '../components/strategy-grid/monaco-widget/monaco-widget.component';
import { StrategyEditorWidgetComponent } from '../components/strategy-grid/strategy-editor-widget/strategy-editor-widget.component';
import { StreamsWidgetComponent } from '../components/strategy-grid/streams-widget/streams-widget.component';
import { CompilerWidgetComponent } from '../components/strategy-grid/compiler-widget/compiler-widget.component';
import { StreamRestWidgetComponent } from '../components/strategy-grid/stream-rest-widget/stream-rest-widget.component';
import { GridPageComponent } from '../components/pages/grid-page/grid-page.component';
import { PerfTableWidgetComponent } from '../components/strategy-grid/perf-table-widget/perf-table-widget.component';
import { AgentWidgetComponent } from '../components/strategy-grid/agent-widget/agent-widget.component';
import { SettingsService } from './settings.service';
import { PromptsWidgetComponent } from '../components/strategy-grid/prompts-widget/prompts-widget.component';
import { IndicatorsWidgetComponent } from '../components/strategy-grid/indicators-widget/indicators-widget.component';
import { TemplateWidgetComponent } from '../components/strategy-grid/template-widget/template-widget.component';
import { JobsWidgetComponent } from '../components/strategy-grid/jobs-widget/jobs-widget.component';
import { LLMWorkflowsWidgetComponent } from '../components/strategy-grid/llmworkflows-widget/llmworkflows-widget.component';
import { WorkflowWidgetComponent } from '../components/strategy-grid/workflow-widget/workflow-widget.component';
import { LicenseWidgetComponent } from '../components/strategy-grid/license-widget/license-widget.component';
import { TeamPolicyWidgetComponent } from '../components/strategy-grid/team-policy-widget/team-policy-widget.component';
import { TrustsWidgetComponent } from '../components/strategy-grid/trusts-widget/trusts-widget.component';
import { AgentsWidgetComponent } from '../components/strategy-grid/agents-widget/agents-widget.component';
import { AgentSettingsWidgetComponent } from '../components/strategy-grid/agent-settings-widget/agent-settings-widget.component';
import { BackendManagerWidgetComponent } from '../components/strategy-grid/backend-manager-widget/backend-manager-widget.component';
import { BackendDetailWidgetComponent } from '../components/strategy-grid/backend-detail-widget/backend-detail-widget.component';
import { TradingViewWidgetComponent } from '../components/strategy-grid/tradingview-widget/tradingview-widget.component';
import { PreviewWidgetComponent } from '../components/strategy-grid/preview-widget/preview-widget.component';
import { IframeWidgetComponent } from '../components/strategy-grid/iframe-widget/iframe-widget.component';
import { DataViewerWidgetComponent } from '../components/strategy-grid/data-viewer-widget/data-viewer-widget.component';
import { PlaygroundDetailWidgetComponent } from '../components/strategy-grid/playground-detail-widget/playground-detail-widget.component';
import { DeployDetailWidgetComponent } from '../components/strategy-grid/deploy-detail-widget/deploy-detail-widget.component';
import { StreamDetailWidgetComponent } from '../components/strategy-grid/stream-detail-widget/stream-detail-widget.component';
import { MarkdownWidgetComponent } from '../components/strategy-grid/markdown-widget/markdown-widget.component';
import { ExternalAppWidgetComponent } from '../components/strategy-grid/external-app-widget/external-app-widget.component';
import { HeartbeatWidgetComponent } from '../components/strategy-grid/heartbeat-widget/heartbeat-widget.component';
import { KnowledgeGraphWidgetComponent } from '../components/strategy-grid/knowledge-graph-widget/knowledge-graph-widget.component';
import { BrowserWidgetComponent } from '../components/strategy-grid/browser-widget/browser-widget.component';

export namespace WidgetService {
  // Since certain Widgets implement a different constructor, we'll ignore that
  export type WidgetComponent = Omit<typeof BaseWidgetComponent, 'constructor'>;
  export type WidgetName = keyof typeof WidgetService.REGISTRY;

  export type Widget = GridsterItem & {
    component: WidgetComponent;
    name: WidgetName;
    id: string;
    inputs: Record<string, any>;
    outputs: Record<string, any>;
    birthday: Date;
    defaultSpace: Pick<Required<GridSpace>, 'rows' | 'cols'>;
    savedCoords: number[];
    isMaximized: boolean;
  };

  export type SplitViewWidget = {
    component: WidgetComponent;
    inputs: Record<string, any>;
    outputs: Record<string, any>;
  };

  export type AxisUpdateEvent = {
    id: string;
    visibleRange: NumberRange;
  };

  export type ChildEvent = { parentId: string };
  export type AddChildEvent = ChildEvent & { id: string };
  export type RemoveChildEvent = AddChildEvent;
  export type LoadModelEvent = ChildEvent;

  export type ParentChartUpdatedEvent = ChildEvent & { chartData: any[] };
  export type TickerUpdateEvent = ChildEvent & { ticker: string };
  export type IntervalUpdateEvent = ChildEvent & { interval: string };
  export type StreamUpdateEvent = ChildEvent & { streamName: string };
  export type StratsUpdateEvent = ChildEvent & { strats: string[] };
  export type StratEditEvent = ChildEvent;
  export type ModelsAdded = { models: string[] };
  export type AnnotationsUpdateEvent = ChildEvent & { myId: string; annotationMap: any[] };

  export type ResizeEvent = {
    item: Widget;
    itemComponent: GridsterItemComponent;
  };

  export type GridSpace = Partial<{
    x: number;
    y: number;
    rows: number;
    cols: number;
    maxItemRows: number;
    minItemRows: number;
    maxItemCols: number;
    minItemCols: number;
    minItemArea: number;
    maxItemArea: number;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class WidgetService {
  static readonly REGISTRY = {
    RestStreamWidget: StreamRestWidgetComponent,
    StreamWidget: StreamWidgetComponent,
    StreamsWidget: StreamsWidgetComponent,
    ModelChartWidget: ModelChartWidgetComponent,
    TickWidget: TickWidgetComponent,
    PacketWidget: PacketWidgetComponent,
    MonacoWidget: MonacoWidgetComponent,
    ModelInputsWidget: ModelInputsWidgetComponent,
    StrategyWidget: StrategyWidgetComponent,
    StrategyEditorWidget: StrategyEditorWidgetComponent,
    BreakoutWidget: BreakoutWidgetComponent,
    ExperimentsWidget: ExperimentsWidgetComponent,
    RawDataWidget: RawDataWidgetComponent,
    FeatureEditorWidget: FeatureEditorWidgetComponent,
    PromptsWidget: PromptsWidgetComponent,
    JobsWidget: JobsWidgetComponent,
    LLMWorkflowsWidget: LLMWorkflowsWidgetComponent,
    TemplateWidget: TemplateWidgetComponent,
    LicenseWidget: LicenseWidgetComponent,
    TeamPolicyWidget: TeamPolicyWidgetComponent,
    TrustsWidget: TrustsWidgetComponent,
    AgentsWidget: AgentsWidgetComponent,
    WorkflowWidget: WorkflowWidgetComponent,
    IndicatorsWidget: IndicatorsWidgetComponent,
    FeatureSelectorWidget: FeatureSelectorWidgetComponent,
    SchemasWidget: SchemasWidgetComponent,
    BuildsWidget: BuildsWidgetComponent,
    BuildDetailWidget: BuildDetailWidgetComponent,
    LabWidget: LabWidgetComponent,
    AssetsWidget: AssetsWidgetComponent,
    InspectWidget: InspectWidgetComponent,
    DefinitionsWidget: DefinitionsWidgetComponent,
    VaultWidget: VaultWidgetComponent,
    VaultDetailWidget: VaultDetailWidgetComponent,
    PlaygroundWidget: PlaygroundWidgetComponent,
    ModelDiffWidget: ModelDiffWidgetComponent,
    DeploysWidget: DeploysWidgetComponent,
    HostsWidget: HostsWidgetComponent,
    FilesWidget: FilesWidgetComponent,
    TerminalWidget: TerminalWidgetComponent,
    GPUsWidget: GpusWidgetComponent,
    ChatWidget: ChatWidgetComponent,
    SparklinesWidget: SparklinesWidgetComponent,
    AgentWidget: AgentWidgetComponent,
    IndicatorWidget: IndicatorWidgetComponent,
    SettingsWidget: SettingsWidgetComponent,
    AgentSettingsWidget: AgentSettingsWidgetComponent,
    BackendManagerWidget: BackendManagerWidgetComponent,
    BackendDetailWidget: BackendDetailWidgetComponent,
    CompilerWidget: CompilerWidgetComponent,
    PerfTableWidget: PerfTableWidgetComponent,
    TradingViewWidget: TradingViewWidgetComponent,
    PreviewWidget: PreviewWidgetComponent,
    IframeWidget: IframeWidgetComponent,
    DataViewerWidget: DataViewerWidgetComponent,
    PlaygroundDetailWidget: PlaygroundDetailWidgetComponent,
    DeployDetailWidget: DeployDetailWidgetComponent,
    StreamDetailWidget: StreamDetailWidgetComponent,
    MarkdownWidget: MarkdownWidgetComponent,
    ExternalAppWidget: ExternalAppWidgetComponent,
    HeartbeatWidget: HeartbeatWidgetComponent,
    KnowledgeGraphWidget: KnowledgeGraphWidgetComponent,
    BrowserWidget: BrowserWidgetComponent,
  } as const;

  public parentXAxisUpdate$: Subject<WidgetService.AxisUpdateEvent> = new Subject();
  public childXAxisUpdate$: Subject<WidgetService.AxisUpdateEvent> = new Subject();
  public childAdd$: Subject<WidgetService.AddChildEvent> = new Subject();
  public childDelete$: Subject<WidgetService.RemoveChildEvent> = new Subject();
  public loadModelData$: Subject<WidgetService.LoadModelEvent> = new Subject();
  public parentChartDataUpdate$: Subject<WidgetService.ParentChartUpdatedEvent> = new Subject();
  public tickerUpdate$: Subject<WidgetService.TickerUpdateEvent> = new Subject();
  public intervalUpdate$: Subject<WidgetService.IntervalUpdateEvent> = new Subject();
  public streamUpdate$: Subject<WidgetService.StreamUpdateEvent> = new Subject();
  public stratsUpdate$: Subject<WidgetService.StratsUpdateEvent> = new Subject();
  public stratEdit$: Subject<WidgetService.StratEditEvent> = new Subject();
  public modelsAdded$: Subject<WidgetService.ModelsAdded> = new Subject();
  public annotationsUpdate$: Subject<WidgetService.AnnotationsUpdateEvent> = new Subject();
  public resize$: Subject<WidgetService.ResizeEvent> = new Subject();
  public restore$: Subject<void> = new Subject();
  public maximize$: Subject<void> = new Subject();
  public playgroundSampleUpdate$: Subject<{
    teamName: string;
    modelName: string;
    sampleIndex: number;
    prediction: number;
    ground_truth: number;
    error: number;
    correct: boolean;
  }> = new Subject();

  public widgets: WidgetService.Widget[] = [];

  public grid: GridPageComponent = null;

  /** Emits when the app-level menubar requests opening a markdown viewer (chat-page fullscreen mode). */
  public readonly viewMarkdownRequest$ = new Subject<{ path: string; title: string; startInEditMode?: boolean }>();

  /** Emits when something requests opening an app by ID (the /open-app skill). */
  public readonly openAppRequest$ = new Subject<{ appId: string; inputs?: Record<string, any> }>();

  /** Emits when the Agents lens wants Agent Settings opened for a specific agent
   *  (agentId=null → create). Handled by the window-manager host (grid-page), which
   *  is always mounted in the workspace — unlike chat-widget. */
  public readonly openAgentSettings$ = new Subject<{ agentId: string | null; team?: string | null }>();

  /** Emits a team slug (or {device}:{team} place address) when something asks
   *  for a chat scoped to that place — palette rows and starred rail pins.
   *  Routed via service, not @Output: TWO menubar instances exist (app shell +
   *  grid-page) and the visible one isn't necessarily the bound one. */
  public readonly openChatForTeam$ = new Subject<string>();
  /** Ask the workspace of a team to show one channel (an app's "Chat" button on
   *  a directory entry, for instance). The request is also kept briefly, so a
   *  workspace that is only now being opened can pick it up when it is ready. */
  public readonly openChannel$ = new Subject<{ team: string; channelId: string }>();
  private _pendingChannel: { team: string; channelId: string; at: number } | null = null;
  public requestChannel(team: string, channelId: string): void {
    this._pendingChannel = { team, channelId, at: Date.now() };
    this.openChatForTeam$.next(team);
    this.openChannel$.next({ team, channelId });
  }
  /** A request made for this team in the last 15 s, consumed once. */
  public takePendingChannel(team: string): string | null {
    const p = this._pendingChannel;
    if (!p || p.team !== team || Date.now() - p.at > 15000) return null;
    this._pendingChannel = null;
    return p.channelId;
  }
  /** Team-app open requests (palette "Agents: abodoo" rows). Same reason as
   *  openChatForTeam$: two menubar instances — @Output from the palette's
   *  instance reaches nobody; the grid subscribes here instead. */
  public readonly openTeamAppFor$ = new Subject<{ name: string; team: string; title?: string }>();

  constructor(private readonly _settings: SettingsService) {}

  registerGrid(grid: GridPageComponent) {
    this.grid = grid;
  }

  createWidget(
    name: WidgetService.WidgetName,
    teamName: string,
    values?: Partial<WidgetService.Widget>,
    config?: Partial<{
      defaultSpace: WidgetService.GridSpace;
      baseInputs: Record<string, any>;
      outputs: Record<string, any>;
    }>
  ): WidgetService.Widget {
    const id = values?.id ?? `${name}${this.widgets.filter(({ id }) => id.startsWith(name)).length + 1}`;

    let nextLayerIndex = 0;
    if (values && values.layerIndex) {
      nextLayerIndex = values.layerIndex;
    } else if (this.widgets.length > 0) {
      nextLayerIndex = Math.max(...this.widgets.map((widget) => (widget.layerIndex ?? 0) + 1));
    }

    const savedCoords = values?.savedCoords ?? [0, 0, 0, 0];
    const isMaximized = savedCoords.reduce((partialSum, a) => partialSum + a, 0) != 0;

    let retval = {
      x: values?.x ?? config?.defaultSpace?.x ?? 1,
      y: values?.y ?? config?.defaultSpace?.y ?? 1,
      rows: values?.rows ?? config?.defaultSpace?.rows ?? 35,
      cols: values?.cols ?? config?.defaultSpace?.cols ?? 55,
      defaultSpace: {
        rows: config?.defaultSpace?.rows ?? 35,
        cols: config?.defaultSpace?.cols ?? 55
      },
      maxItemRows: values?.maxItemRows ?? config?.defaultSpace?.maxItemRows,
      minItemRows: values?.minItemRows ?? config?.defaultSpace?.minItemRows,
      maxItemCols: values?.maxItemCols ?? config?.defaultSpace?.maxItemCols,
      minItemCols: values?.minItemCols ?? config?.defaultSpace?.minItemCols,
      minItemArea: values?.minItemArea ?? config?.defaultSpace?.minItemArea,
      maxItemArea: values?.maxItemArea ?? config?.defaultSpace?.maxItemArea,
      layerIndex: nextLayerIndex,
      component: this.nameToComponent(name),
      name,
      id,
      inputs: { teamName, id, isMaximized, ...(config?.baseInputs ?? {}), ...(values?.inputs ?? {}) },
      outputs: config?.outputs ?? {},
      birthday: new Date(),
      savedCoords,
      isMaximized: false
    };

    retval.isMaximized = isMaximized;
    return retval;
  }

  nameToComponent(name: WidgetService.WidgetName): WidgetService.WidgetComponent {
    if (name in WidgetService.REGISTRY) return WidgetService.REGISTRY[name];

    console.warn(`unknown widget: ${name}`);
    return BaseWidgetComponent;
  }

  addWidget(teamName: string, widget: WidgetService.Widget) {
    this.widgets.push(widget);
    this.save(teamName);
  }

  removeWidget(teamName: string, ...ids: string[]) {
    for (const id of ids) {
      const found = this.widgets.findIndex((widget) => widget.id === id);
      if (found === -1) continue;
      this.widgets.splice(found, 1);
    }
    this.save(teamName);
  }

  updateWidget(teamName: string, id: string, widget: WidgetService.Widget) {
    const found = this.widgets.findIndex(({ id: widgetID }) => widgetID === id);
    if (found === -1) return;

    this.widgets[found] = widget;
    this.save(teamName);
  }

  patchWidget(teamName: string, id: string, widget: Partial<WidgetService.Widget>) {
    const found = this.widgets.findIndex(({ id: widgetID }) => widgetID === id);
    if (found === -1) return;

    Object.assign(this.widgets[found], widget);
    this.save(teamName);
  }

  maximizeWidget(teamName: string, id: string) {
    const found = this.widgets.find((widget) => widget.id === id);
    if (!found) return;

    if (!found.isMaximized) {
      // I'll maximize twice if you ask, but I'll keep the first set of coordinates
      found.savedCoords = [found.x, found.y, found.cols, found.rows];
    }

    found.x = 0;
    found.y = 0;
    found.isMaximized = true;
    found.inputs.isMaximized = true;

    let { innerHeight, innerWidth } = window;
    if (this._settings.menubarPosition === 'top') {
      innerHeight -= 56;
    } else if (this._settings.menubarPosition === 'left') {
      innerWidth -= 56;
    }

    found.cols = Math.floor(innerWidth / this.grid.options.fixedColWidth);
    found.rows = Math.floor(innerHeight / this.grid.options.fixedRowHeight);

    this.maximize$.next();
    this.save(teamName);
  }

  toggleWidget(teamName: string, id: string) {
    const found = this.widgets.find((widget) => widget.id === id);
    if (!found) return;

    if (found.isMaximized) {
      this.restoreWidget(teamName, id);
    } else {
      this.maximizeWidget(teamName, id);
    }
  }

  restoreWidget(teamName: string, id: string) {
    const found = this.widgets.find((widget) => widget.id === id);
    if (!found) return;

    if (found.savedCoords.reduce((partialSum, a) => partialSum + a, 0) == 0) {
      found.cols = found.defaultSpace.cols;
      found.rows = found.defaultSpace.rows;
    } else {
      found.x = found.savedCoords[0];
      found.y = found.savedCoords[1];
      found.cols = found.savedCoords[2];
      found.rows = found.savedCoords[3];
    }
    found.savedCoords = [0, 0, 0, 0];
    found.isMaximized = false;
    found.inputs.isMaximized = false;

    this.restore$.next();
    this.save(teamName);
  }

  save(teamName: string) {
    localStorage.setItem(`${teamName}-widgets`, JSON.stringify(this.widgets));
  }

  dockWidget(teamName: string, id: string, position: any) {
    if (this.grid) {
      this.grid.dockWidget(id, position);
    }
  }

  undockWidget(teamName: string, id: string) {
    if (this.grid) {
      this.grid.undockWidget(id);
    }
  }
}
